import React from 'react';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { MembershipItem, MembershipItemType, MEMBERSHIP_ITEM_TYPES } from '@spike/membership-model';
import { useSelector } from 'react-redux';
import Service from 'model/Service';
import { RootState } from 'store';
import clsx from 'clsx';
import { CounterField } from 'components/UI/V2/CounterField/CounterField';
import { Checkbox } from 'components/UI/V2/Forms/Checkbox/Checkbox';
import { PriceField2 } from 'components/UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { CollapsibleAlert } from 'components/UI/V2/CollapsibleAlert/CollapsibleAlert';

interface MembershipItemsTableItemProps {
    item: MembershipItem;
    className?: string;
    onDelete?: (uuid: string) => void;
    onChange?: (item: MembershipItem) => void;
    currentItems: MembershipItem[];
    excludedItemIDs: string[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            padding: '24px 0px',
            borderBottom: '1px solid #D4D4D4',

            [theme.breakpoints.up('md')]: {
                padding: '16px 0px'
            },
            [theme.breakpoints.down('sm')]: {
                '&:first-child': {
                    paddingTop: 0
                }
            }
        },
        container: {
            width: '100%',
            alignItems: 'center',
            position: 'relative'
        },
        itemName: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 600,

            [theme.breakpoints.up('md')]: {
                fontWeight: 500
            }
        },
        itemType: {
            fontSize: 14,
            lineHeight: 1,
            marginTop: 4,
            color: '#7A7A7A'
        },
        itemNameColumn: {
            order: 1,

            [theme.breakpoints.down('sm')]: {
                gridColumn: 'span 2'
            }
        },
        quantityColumn: {
            gap: 8,
            order: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',

            [theme.breakpoints.down('sm')]: {
                order: 3,
                alignSelf: 'flex-end',
                justifySelf: 'flex-start'
            }
        },
        quantityField: {
            gap: '4px !important'
        },
        unlimitedCheckbox: {
            '& div': {
                gap: 6
            },

            '& p': {
                fontSize: 14
            }
        },
        priceColumn: {
            order: 3,

            [theme.breakpoints.down('sm')]: {
                order: 4
            }
        },
        priceLabel: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        priceField: {
            '& .MuiInputBase-root': {
                paddingLeft: 0
            },
            '& .MuiInputBase-input': {
                padding: '10px 10px 10px 0px',

                [theme.breakpoints.up('md')]: {
                    padding: '12.5px 10px 12.5px 0px'
                }
            },
            '& .MuiInputAdornment-positionStart': {
                paddingLeft: 10,
                paddingRight: 0
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D3D3D3 !important'
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 1,
                borderColor: '#000000 !important'
            }
        },
        totalColumn: {
            order: 4,
            textAlign: 'right',

            [theme.breakpoints.down('sm')]: {
                order: 5
            }
        },
        totalLabel: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 18,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        totalAmount: {
            fontSize: 15,
            lineHeight: 1,
            fontWeight: 400
        },
        deleteButtonContainer: (props: MembershipItemsTableItemProps) => ({
            textAlign: 'right',
            order: props.item.itemID ? 3 : 0,
            gridColumn: props.item.itemID ? 'span 1' : 'span 3',

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        }),
        deleteButton: {
            'gap': 4,
            'alignItems': 'center',
            'display': 'inline-flex',
            'fontFamily': 'Poppins',
            'fontSize': 14,
            'lineHeight': 1,
            'fontWeight': 500,
            'color': '#EF4F57',
            'backgroundColor': 'transparent',
            'border': 'none',
            'cursor': 'pointer',

            '& span': {
                paddingBottom: 2,
                borderBottom: 'solid 1px #EF4F57'
            }
        },
        desktopDeleteButton: {
            right: -32,
            fontSize: 18,
            display: 'none',
            position: 'absolute',
            appearance: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            border: 'none',

            [theme.breakpoints.up('md')]: {
                display: 'block'
            }
        },
        alert: {
            order: 99,
            marginTop: 24,
            gridColumn: 'span 4'
        },
        serviceVariablePrices: {
            gap: 16,
            display: 'flex',
            margin: '0px -16px',
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                gap: 24,
                margin: 0,
                display: 'inline-flex'
            }
        },
        serviceVariablePrice: {
            'gap': 16,
            'display': 'grid',
            'gridTemplateColumns': '35px 55px 85px 1fr',

            [theme.breakpoints.up('md')]: {
                gap: 32,
                gridTemplateColumns: '35px 60px 1fr 1fr'
            },

            '& p': {
                'fontSize': 13,
                'lineHeight': 1.4,
                'fontWeight': 400,

                [theme.breakpoints.down('sm')]: {
                    '&:last-child': {
                        textAlign: 'right'
                    }
                },
                [theme.breakpoints.up('md')]: {
                    fontSize: 14
                },

                '& b': {
                    fontWeight: 600
                }
            }
        }
    })
);

export const MembershipItemsTableItem: React.FC<MembershipItemsTableItemProps> = props => {
    const classes = useStyles(props);

    const servicesState = useSelector((state: RootState) => state.services);

    // Filter services by business area (access from servicesByBusinessArea for consistency)
    const groomingServices = React.useMemo(() => {
        const grooming = servicesState.servicesByBusinessArea?.grooming?.services || [];
        const dental = servicesState.servicesByBusinessArea?.dental?.services || [];
        return [...grooming, ...dental];
    }, [servicesState.servicesByBusinessArea?.grooming, servicesState.servicesByBusinessArea?.dental]);

    const daycareServices = React.useMemo(() => {
        return servicesState.servicesByBusinessArea?.daycare?.services || [];
    }, [servicesState.servicesByBusinessArea?.daycare]);

    const boardingServices = React.useMemo(() => {
        return servicesState.servicesByBusinessArea?.boarding?.services || [];
    }, [servicesState.servicesByBusinessArea?.boarding]);

    const walkingServices = React.useMemo(() => {
        return servicesState.servicesByBusinessArea?.walking?.services || [];
    }, [servicesState.servicesByBusinessArea?.walking]);

    const transportationServices = React.useMemo(() => {
        return servicesState.servicesByBusinessArea?.transportation?.services || [];
    }, [servicesState.servicesByBusinessArea?.transportation]);

    const trainingServices = React.useMemo(() => {
        return servicesState.servicesByBusinessArea?.training?.services || [];
    }, [servicesState.servicesByBusinessArea?.training]);

    const service = React.useMemo(() => {
        if (!props.item.itemID) return undefined;
        const id = props.item.itemID;
        switch (props.item.itemType) {
            case MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE:
            case MembershipItemType.SERVICE:
                return groomingServices.find(s => String(s.id) === id);
            case MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE:
                return daycareServices.find(s => String(s.id) === id);
            case MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE:
                return boardingServices.find(s => String(s.id) === id);
            case MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE:
                return walkingServices.find(s => String(s.id) === id);
            case MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE:
                return transportationServices.find(s => String(s.id) === id);
            case MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE:
                return trainingServices.find(s => String(s.id) === id);
            default:
                return undefined;
        }
    }, [
        props.item.itemID,
        props.item.itemType,
        groomingServices,
        daycareServices,
        boardingServices,
        walkingServices,
        transportationServices,
        trainingServices
    ]);

    const isVariablePrice = React.useMemo(() => {
        const isServiceType =
            props.item.itemType === MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE ||
            props.item.itemType === MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE ||
            props.item.itemType === MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE ||
            props.item.itemType === MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE ||
            props.item.itemType === MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE ||
            props.item.itemType === MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE ||
            props.item.itemType === MembershipItemType.SERVICE;

        if (isServiceType) {
            return (
                service?.pricing.variablePrice?.variables &&
                Object.keys(service.pricing.variablePrice.variables).length > 0
            );
        }

        return false;
    }, [props.item, service]);

    const itemTypeLabel = React.useMemo(() => {
        switch (props.item.itemType) {
            case MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE:
                return 'Grooming Service';
            case MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE:
                return 'Daycare Service';
            case MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE:
                return 'Boarding Service';
            case MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE:
                return 'Walking Service';
            case MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE:
                return 'Transportation Service';
            case MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE:
                return 'Training Service';
            case MEMBERSHIP_ITEM_TYPES.PRODUCT:
                return 'Product';
            case MEMBERSHIP_ITEM_TYPES.ADD_ON:
                return 'Add-On';
            // Backward compatibility with old data
            case MembershipItemType.SERVICE:
            case 'Primary Service':
                return 'Service';
            default:
                return 'Service';
        }
    }, [props.item.itemType]);

    const totalItemPrice = React.useMemo(() => {
        if (props.item.unlimited) {
            return props.item.amount.toFixed(2);
        }

        return Number(props.item.amount * props.item.quantity).toFixed(2);
    }, [props.item.amount, props.item.quantity, props.item.unlimited]);

    const changeItemPriceHandler = (value: number | undefined) => {
        if (value === undefined) {
            props.onChange?.({ ...props.item, amount: 0 });

            return;
        }

        props.onChange?.({ ...props.item, amount: value });
    };

    return (
        <div className={classes.root}>
            <div className={clsx(classes.container, props.className)}>
                <div className={classes.deleteButtonContainer}>
                    <button
                        type="button"
                        className={classes.deleteButton}
                        onClick={() => props.onDelete?.(props.item.uuid || '')}
                    >
                        <FontAwesomeIcon icon={faTrashAlt} />
                        <span>Delete</span>
                    </button>
                </div>
                <button
                    type="button"
                    className={classes.desktopDeleteButton}
                    onClick={() => props.onDelete?.(props.item.uuid || '')}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <div className={classes.itemNameColumn}>
                    <Typography className={classes.itemName}>{props.item.itemName}</Typography>
                    <Typography className={classes.itemType}>{itemTypeLabel}</Typography>
                </div>
                <div className={classes.quantityColumn}>
                    <CounterField
                        minLimit={1}
                        value={props.item.quantity}
                        disabled={props.item.unlimited}
                        className={classes.quantityField}
                        onChange={value => props.onChange?.({ ...props.item, quantity: value })}
                    />
                    <Checkbox
                        inlineLabel="Unlimited"
                        checked={props.item.unlimited}
                        className={classes.unlimitedCheckbox}
                        onChange={value => props.onChange?.({ ...props.item, unlimited: value, quantity: 1 })}
                    />
                </div>
                <div className={classes.priceColumn}>
                    <Typography className={classes.priceLabel}>Price</Typography>
                    <PriceField2
                        scale={2}
                        value={props.item.amount}
                        className={classes.priceField}
                        onChange={changeItemPriceHandler}
                    />
                </div>
                <div className={classes.totalColumn}>
                    <Typography className={classes.totalLabel}>Total</Typography>
                    <Typography className={classes.totalAmount}>${totalItemPrice}</Typography>
                </div>
            </div>

            {/* TODO: This is a temporary solution to display the variable pricing. */}
            {service && isVariablePrice && (
                <CollapsibleAlert
                    title="This service's pricing varies by pet size:"
                    className={classes.alert}
                >
                    <div className={classes.serviceVariablePrices}>
                        {(() => {
                            // Group prices by pet type and size
                            const groupedPrices = service.pricing.variablePrice?.prices.reduce((acc, variable) => {
                                const key = `${variable.petType?.plural}-${variable.petSize?.name}`;
                                if (!acc[key]) {
                                    acc[key] = [];
                                }
                                acc[key].push(variable);
                                return acc;
                            }, {} as Record<string, typeof service.pricing.variablePrice.prices>);
                            return Object.entries(groupedPrices || {}).map(([key, variables]) => (
                                <div key={key}>
                                    {variables.map(variable => (
                                        <div
                                            key={variable.uuid}
                                            className={classes.serviceVariablePrice}
                                        >
                                            <Typography>{variable.petType?.plural}</Typography>
                                            <Typography>{variable.petSize?.name}</Typography>
                                            <Typography>{variable.hairLength?.name}</Typography>
                                            <Typography>
                                                <b>${variable.price?.toFixed(2)}</b>
                                            </Typography>
                                        </div>
                                    ))}
                                </div>
                            ));
                        })()}
                    </div>
                </CollapsibleAlert>
            )}
        </div>
    );
};
